import { useState, useEffect, useRef } from 'react';
import { ShieldAlert, AlertTriangle, Clock, Bell, RefreshCw, ArrowRight, PackageX, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRole } from '../../hooks/useRole';
import { api } from '../../utils/api';
import { Button } from '../ui';

interface AdminAlertsData {
  totalAlerts: number;
  criticalStock: {
    id: number;
    name: string;
    currentStock: number;
    minStock: number;
    unit: string;
    isExhausted: boolean;
  }[];
  delayedOrders: {
    id: number;
    tableName: string;
    status: string;
    elapsedMinutes: number;
    itemsCount: number;
    createdAt: string;
  }[];
  delayedRequests: {
    id: number;
    tableName: string;
    type: string;
    elapsedMinutes: number;
    createdAt: string;
  }[];
  cashAlert: {
    sessionId: number;
    hoursOpen: number;
    warning: string;
  } | null;
  checkedAt: string;
}

export function AdminAlertsBell() {
  const { isAdmin } = useRole();
  const [data, setData] = useState<AdminAlertsData | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // RESTRICCIÓN ESTRICTA: Solo para perfil de Administrador
  if (!isAdmin) {
    return null;
  }

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res = await api.get<AdminAlertsData>('/dashboard/admin-alerts');
      setData(res.data);
    } catch (err) {
      console.warn('Error fetching admin live alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 40000); // Consulta cada 40s
    return () => clearInterval(interval);
  }, []);

  // Cerrar al hacer clic afuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const total = data?.totalAlerts ?? 0;
  const hasCritical = (data?.criticalStock?.length ?? 0) > 0 || (data?.delayedOrders?.length ?? 0) > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botón Campana de Alertas de Administrador */}
      <Button
        variant="ghost"
        size="sm"
        aria-label="Centro de alertas en vivo de administrador"
        onClick={() => setOpen((prev) => !prev)}
        className={`relative h-9 w-9 rounded-xl border transition-all ${
          total > 0
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-500 hover:bg-amber-500/20'
            : 'border-border text-text-muted hover:bg-primary/10 hover:text-primary'
        }`}
      >
        <ShieldAlert className="h-4 w-4" />
        {total > 0 && (
          <span
            className={`absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white shadow-sm ${
              hasCritical ? 'bg-rose-500 animate-pulse' : 'bg-amber-500'
            }`}
          >
            {total > 9 ? '9+' : total}
          </span>
        )}
      </Button>

      {/* Popover flotante de Alertas */}
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-84 sm:w-96 overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border bg-surface-muted/60 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-amber-500/15 p-1.5 text-amber-500">
                <ShieldAlert className="h-4 w-4" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-text">Centro de Alertas</span>
                  <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[9px] font-extrabold text-black">ADMIN</span>
                </div>
                <p className="text-[11px] text-text-muted">Supervisión operativa en tiempo real</p>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={fetchAlerts}
              disabled={loading}
              className="h-7 w-7 rounded-lg p-0 text-text-muted hover:text-text"
              title="Actualizar alertas"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Contenido scrolleable */}
          <div className="max-h-[380px] overflow-y-auto divide-y divide-border/60">
            {total === 0 ? (
              <div className="py-8 text-center px-4">
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500">
                  ✓
                </div>
                <p className="text-sm font-semibold text-text">Operación en orden</p>
                <p className="text-xs text-text-muted mt-0.5">
                  No hay stock crítico, comandas demoradas ni llamados de mesa pendientes.
                </p>
              </div>
            ) : (
              <>
                {/* 1. Alerta de Caja */}
                {data?.cashAlert && (
                  <div className="p-3 bg-rose-500/5">
                    <div className="flex items-start gap-2.5">
                      <div className="rounded-md bg-rose-500/15 p-1 text-rose-500 mt-0.5">
                        <AlertTriangle className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-rose-600 dark:text-rose-400">
                          Caja abierta por {data.cashAlert.hoursOpen}h
                        </p>
                        <p className="text-[11px] text-text-muted mt-0.5">{data.cashAlert.warning}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* 2. Comandas Demoradas en Cocina */}
                {data?.delayedOrders && data.delayedOrders.length > 0 && (
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-1.5 text-xs font-bold text-amber-500">
                        <Clock className="h-3.5 w-3.5" />
                        Comandas Demoradas ({data.delayedOrders.length})
                      </span>
                      <Link
                        to="/orders"
                        onClick={() => setOpen(false)}
                        className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-0.5"
                      >
                        Ver pedidos <ExternalLink className="h-2.5 w-2.5" />
                      </Link>
                    </div>
                    <div className="space-y-1.5">
                      {data.delayedOrders.map((o) => (
                        <div
                          key={o.id}
                          className="flex items-center justify-between rounded-xl bg-surface-muted/70 px-2.5 py-1.5 text-xs"
                        >
                          <div>
                            <span className="font-semibold text-text">{o.tableName}</span>
                            <span className="text-[11px] text-text-muted ml-1.5">({o.itemsCount} productos)</span>
                          </div>
                          <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-bold text-rose-600 dark:text-rose-400">
                            ⏱️ {o.elapsedMinutes} min
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 3. Mesas esperando atención o cuenta */}
                {data?.delayedRequests && data.delayedRequests.length > 0 && (
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-1.5 text-xs font-bold text-indigo-500">
                        <Bell className="h-3.5 w-3.5" />
                        Llamados sin atender ({data.delayedRequests.length})
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {data.delayedRequests.map((req) => (
                        <div
                          key={req.id}
                          className="flex items-center justify-between rounded-xl bg-surface-muted/70 px-2.5 py-1.5 text-xs"
                        >
                          <div>
                            <span className="font-semibold text-text">{req.tableName}</span>
                            <span className="text-[11px] text-text-muted ml-1.5">
                              {req.type === 'BILL' ? 'Solicitó la cuenta' : 'Llama al mesero'}
                            </span>
                          </div>
                          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:text-amber-400">
                            {req.elapsedMinutes} min esperando
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 4. Stock Crítico */}
                {data?.criticalStock && data.criticalStock.length > 0 && (
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex items-center gap-1.5 text-xs font-bold text-rose-500">
                        <PackageX className="h-3.5 w-3.5" />
                        Insumos por agotarse ({data.criticalStock.length})
                      </span>
                      <Link
                        to="/inventory"
                        onClick={() => setOpen(false)}
                        className="text-[11px] font-semibold text-primary hover:underline flex items-center gap-0.5"
                      >
                        Inventario <ExternalLink className="h-2.5 w-2.5" />
                      </Link>
                    </div>
                    <div className="space-y-1.5">
                      {data.criticalStock.slice(0, 6).map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between rounded-xl bg-surface-muted/70 px-2.5 py-1.5 text-xs"
                        >
                          <div className="truncate max-w-[190px]">
                            <span className="font-semibold text-text truncate">{item.name}</span>
                          </div>
                          <div className="text-right">
                            <span
                              className={`text-[11px] font-bold ${
                                item.isExhausted ? 'text-rose-500' : 'text-amber-500'
                              }`}
                            >
                              {item.currentStock} {item.unit}
                            </span>
                            <span className="text-[10px] text-text-muted ml-1">(mín: {item.minStock})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border bg-surface-muted/40 p-2 text-center">
            <p className="text-[10px] text-text-muted">
              Visible únicamente para administradores • Último chequeo: {data?.checkedAt ? new Date(data.checkedAt).toLocaleTimeString() : '--'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
